import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { getAllPermissions } from '../services/api/permissionsApi';
import { getUserPermissionsList, updateUserPermissions } from '../services/api/userApi';

const UserPermissions = ({ user, onClose }) => {
  const [allPermissions, setAllPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchData();
  }, [user.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [allPermsResponse, userPermsResponse] = await Promise.all([
        getAllPermissions(),
        getUserPermissionsList(user.id)
      ]);
      setAllPermissions(allPermsResponse.data);
      setUserPermissions(userPermsResponse.data);
      setSelectedPermissions(userPermsResponse.data);
    } catch (err) {
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionsChange = (selectedOptions) => {
    const selected = selectedOptions ? selectedOptions.map(opt => 
      allPermissions.find(p => p.id === opt.value)
    ) : [];
    setSelectedPermissions(selected);
  };

  const confirmSave = () => {
    setShowSaveModal(true);
  };

  const isDirty = () => {
    const selectedIds = selectedPermissions.map(p => p.id).sort();
    const currentIds = userPermissions.map(p => p.id).sort();
    return JSON.stringify(selectedIds) !== JSON.stringify(currentIds);
  };

  const handleCancel = () => {
    if (isDirty()) {
      setShowCancelModal(true);
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    const selectedIds = selectedPermissions.map(p => p.id);
    
    try {
      await updateUserPermissions(user.id, selectedIds);
      setUserPermissions(selectedPermissions);
      setShowSaveModal(false);
      setSuccess('Permissions updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update permissions');
      setShowSaveModal(false);
    }
  };

  if (loading) return <div>Loading permissions...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="user-permissions">
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      
      <div className="user-permissions-header">
        <h3>Manage Permissions for {user.email}</h3>
      </div>
      
      <div className="permissions-list">
        <Select
          isMulti
          options={Object.entries(
            allPermissions.reduce((groups, perm) => {
              const resource = perm.resource;
              if (!groups[resource]) groups[resource] = [];
              groups[resource].push({
                value: perm.id,
                label: `${perm.name} (${perm.action})`
              });
              return groups;
            }, {})
          ).map(([resource, options]) => ({
            label: resource,
            options: options.sort((a, b) => a.label.localeCompare(b.label))
          })).sort((a, b) => a.label.localeCompare(b.label))}
          value={selectedPermissions.map(perm => ({
            value: perm.id,
            label: perm.name
          }))}
          onChange={handlePermissionsChange}
          placeholder="Select permissions..."
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          isSearchable
        />
      </div>

      <div className="mt-3">
        <button onClick={confirmSave} className="btn btn-primary me-2" disabled={!isDirty()}>Save</button>
        <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
      </div>

      {/* Save Confirmation Modal */}
      {showSaveModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Save</h5>
                <button type="button" className="btn-close" onClick={() => setShowSaveModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Save permission changes for <strong>{user.email}</strong>?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSaveModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Unsaved Changes</h5>
                <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>You have unsaved changes. Are you sure you want to cancel?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>Stay</button>
                <button type="button" className="btn btn-danger" onClick={onClose}>Discard Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPermissions;